import './index.less';

import { Modal } from '@bfe/moly';
import classNames from 'classnames';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from 'react';

import { useUserProfile } from '@/hooks/useUserProfile';
import { useI18nContext } from '@/i18n/I18n';
import { IBaseResponse, UserInfo } from '@/services/types/common';
import { accountType } from '@/utils/pageConfig/subAccount';
import {
  getCreateSubAccountTypeFromSearchParams,
  isShowCreateSubAccount,
} from '@/utils/pageConfig/subAccount/helper/action';

import SelectAccountType from './SelectAccountType';
import TipGaBindModal from './TipGaBindModal';
interface ICreateSubAccount {
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  user: IBaseResponse<UserInfo> | undefined;
  type: accountType;
  updateList: () => void;
  setSwitchListChecked: Dispatch<SetStateAction<boolean>>;
  directly?: boolean;
}

type SelectSubAccount = Omit<ICreateSubAccount, 'type'>;

const initCreateModal = ({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (subAccountInfo: {
    memberId: string;
    hasPassword: boolean;
    name: string;
    is_islamic: boolean;
  }) => void;
}) => {
  window.BybitUniFrame?.ready((props: { createSubAccountModal: any }) => {
    props?.createSubAccountModal?.init().then((createModal: any) => {
      const { CLOSE_MODAL, CREATED_SUCCESS } = createModal.events;
      const onSuccessCallback = (subAccountInfo: {
        memberId: string;
        hasPassword: boolean;
        name: string;
        is_islamic: boolean;
      }) => {
        onSuccess(subAccountInfo);
      };
      const onCloseCallback = () => {
        onClose();
      };
      createModal
        .subscribeEvent(CLOSE_MODAL, onCloseCallback)
        .subscribeEvent(CREATED_SUCCESS, onSuccessCallback);
    });
  });
};

export const showCreateModal = ({
  visible,
  type = accountType.COMMON,
  userInfo,
}: {
  visible: boolean;
  type: accountType;
  userInfo: UserInfo | null;
}) => {
  const { id, member_tag, unified_account } = userInfo || {};
  const userType =
    member_tag?.UTA === 'SUCCESS'
      ? 'uta'
      : unified_account === 2
      ? 'uma'
      : 'classic';

  window.BybitUniFrame?.ready((props: { createSubAccountModal: any }) => {
    props?.createSubAccountModal?.init().then((createModal: any) => {
      const { VISIBLE, ACCOUNT_TYPE, USER_INFO, IS_TRANSFER_ASSETS } =
        createModal.properties;
      createModal
        .setProperty(ACCOUNT_TYPE, type)
        .setProperty(USER_INFO, {
          uid: id,
          type: userType,
        })
        .setProperty(VISIBLE, visible)
        .setProperty(IS_TRANSFER_ASSETS, false);
    });
  });
};

interface IMemberInfo {
  loginName: string;
  memberId: string;
}

// 选择创建子账号类型弹窗
const SelectAccountTypeModal: FC<SelectSubAccount> = ({
  visible,
  setVisible,
  user,
  updateList,
  setSwitchListChecked,
}) => {
  const [$t] = useI18nContext();
  const [subVisible, setSubVisible] = useState(
    isShowCreateSubAccount() === 'createModal',
  );
  const [type, setType] = useState<accountType>(
    getCreateSubAccountTypeFromSearchParams().createAccountType ||
      accountType.UNSELECT,
  );
  const [showGaVerifyTipModal, setShowGaVerifyTipModal] = useState(false);
  const [memberInfo, setMemberInfo] = useState<IMemberInfo | null>(null);
  const { updateUserProfile } = useUserProfile();

  const onSuccess = () => {
    updateList();
    setSwitchListChecked(false);
  };

  useEffect(() => {
    initCreateModal({
      onClose: () => {
        setSubVisible(false);
        updateList();
      },
      onSuccess: ({ hasPassword, name, memberId, is_islamic }) => {
        setMemberInfo({ loginName: name, memberId });
        if (is_islamic) {
          updateUserProfile();
        }
        if (hasPassword) {
          setShowGaVerifyTipModal(true);
        } else {
          onSuccess();
        }
      },
    });
  }, []);

  useEffect(() => {
    showCreateModal({
      visible: subVisible,
      type,
      userInfo: user?.result || null,
    });
  }, [subVisible]);

  return (
    <>
      <Modal
        title={$t('child.createASubaccount')}
        open={visible}
        className={classNames(
          'select-account-type-modal',
          visible ? 'select-account-type-open' : 'select-account-type-close',
        )}
        overlayClassName={classNames(
          'select-account-type-modal-mask',
          visible ? 'select-account-type-open' : 'select-account-type-close',
        )}
        width={480}
        onCancel={() => {
          setVisible(false);
        }}
        okText={false}
        cancelText={false}
      >
        <SelectAccountType
          handleVisible={() => {
            setVisible(false);
            setSubVisible(true);
          }}
          setType={setType}
          userInfo={user?.result}
        />
      </Modal>
      <TipGaBindModal
        memberInfo={memberInfo as IMemberInfo}
        showGaVerifyTipModal={showGaVerifyTipModal && !!memberInfo}
        onClose={() => {
          setShowGaVerifyTipModal(false);
          onSuccess();
        }}
      />
    </>
  );
};

export default SelectAccountTypeModal;
